/* eslint-disable import/prefer-default-export */
import { sameAnswer } from '../../CustomFieldValues/perTicketAnswers';

import {
  CONFIRM_CHANGED,
  PROMO_CODE_NAME_CHANGED,
  PROMO_CODE_NAME_VALID_CHANGED,
  PROMO_CODE_CHANGED,
  TICKET_RESERVATION_CHANGED,
  TICKET_TYPES_CHANGED,
  CANCEL_CHECKOUT,
  STRIPE_PROMISE_CHANGED,
  STRIPE_ERROR_CHANGED,
  CHECKOUT_TIME_PERCENTAGE_REMAINING_CHANGED,
  TIME_LIMIT_REACHED_CHANGED,
  IS_PLACING_ORDER_CHANGED,
  ADD_ONS_CHANGED,
  GTAG_CHANGED,
  CUSTOM_FIELD_VALUE_CHANGED,
  SEATS_IO_CHART_CHANGED,
  SEATS_IO_SELECTED_OBJECTS_CHANGED,
  CUSTOMERIO_ID_CHANGED,
  PARENT_URL_CHANGED,
  ORIGIN_ATTRIBUTION_CHANGED,
  META_CLICK_ID_CHANGED,
  ATTRIBUTION_VISITOR_CHANGED,
  IS_CREATING_TICKET_RESERVATION_CHANGED,
  CART_START_BUSY_MESSAGE_CHANGED,
  INSURANCE_OFFER_STATE_CHANGED,
  TICKET_INSURANCE_CHANGED,
  HAS_INSURANCE_QUOTE_CHANGED,
  DONATION_CUSTOM_CHARGE_TYPE_CHANGED,
  TICKET_GROUPS_CHANGED,
  REVEALED_HOLD_CHANGED,
  USER_CONTEXT_CHANGED,
  MEMBERSHIP_PRODUCTS_CHANGED,
  SELECTED_MEMBERSHIP_PRODUCT_CHANGED,
  WIDGET_CONFIG_REPLACED,
  CHECKOUT_CATEGORIES_REPLACED,
  RESALE_LISTINGS_FETCHED,
  RESALE_QUANTITY_CHANGED
} from '../constants/webOrderFormConstants';

import {
  customFieldValueToParams
} from './../../CustomFieldValues/actions/customFieldValuesActionCreators';

import { RealtimeDataMutator } from '../../RealtimeDataMutator/RealtimeDataMutator';
import { withHeldAddOnCounts } from '../utils/heldAddOns';

const axios = require('axios').default;
import { toast } from 'react-toastify';
import { sharedAttributionVisitorId } from '../../../shared/attribution/useAttributionVisitor';
import { requestBodyWithFiles } from '../../../shared/multipartRequestBody';
import { readChannelsByEvent } from '../../../shared/channelsByEvent';
import { cartIsResaleOnly } from '../utils/cartHasResale';
import clearHiddenTiersInCalculator from '../utils/clearHiddenTiersInCalculator';
import { visibleAddOns } from '../utils/visibleAddOns';
import { clearUnmetAddOnRequirements } from '../utils/addOnQuantityOptions';

const extractRevealedHoldId = (revealedHold) => {
  return revealedHold?.id ? [revealedHold.id] : [];
};

const extractRevealedHoldToken = (revealedHold) => {
  return revealedHold?.token ? [revealedHold.token] : [];
};

export const activeRevealedHold = (linkRevealedHold, promoCode) => {
  if (linkRevealedHold?.id) {
    const quantities = (linkRevealedHold.items || [linkRevealedHold]).map(item => item.quantity);
    if (quantities.some(quantity => quantity > 0) && promoCode?.general_admission_hold_ids?.includes(linkRevealedHold.id)) {
      return { ...linkRevealedHold, capped_by_hold: true };
    }

    return linkRevealedHold;
  }

  return promoCode?.revealed_hold?.id ? promoCode.revealed_hold : {};
};

// https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

function validateZip(zip) {
  const re = /^\d{5}(-\d{4})?$/;
  return re.test(String(zip));
}

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

export const confirmChanged = (confirm) => ({
  type: CONFIRM_CHANGED,
  confirm
});

export const ticketGroupsChanged = (ticketGroups) => ({
  type: TICKET_GROUPS_CHANGED,
  ticketGroups
});

const toastError = (message) => {
  toast.error(message, {
    position: toast.POSITION.TOP_CENTER,
    draggable: false,
    closeOnClick: false,
    autoClose: 5000,
    hideProgressBar: true
  });
}

const handleReservationError = (error) => {
  if (error.response?.status === 409 && error.response?.data?.error === "prices_updated") {
    toast.error(error.response.data.message || "Prices have updated. Please reload to continue.", {
      position: toast.POSITION.TOP_CENTER,
      draggable: false,
      closeOnClick: false,
      autoClose: false,
      hideProgressBar: true,
      onClick: () => window.location.reload()
    });
    return;
  }

  const message = Array.isArray(error.response?.data)
    ? error.response.data.join(", ")
    : "Something went wrong. Please try again.";

  toastError(message);
}

const CART_START_RETRY_DELAYS_MS = [1000, 2000];

const cartStartBusy = (error) => (
  error.response?.status === 409 && error.response?.headers?.["x-checkout-outcome"] === "retry"
);

const postCartStart = (url, body, attempt = 0) => (
  axios.post(url, body).catch((error) => {
    if (attempt >= CART_START_RETRY_DELAYS_MS.length || !cartStartBusy(error)) {
      throw error;
    }

    return new Promise((resolve) => setTimeout(resolve, CART_START_RETRY_DELAYS_MS[attempt]))
      .then(() => postCartStart(url, body, attempt + 1));
  })
);

export const channelMsgReceived = (calculatorRef) => {
  return (data) => {
    return (dispatch, getState) => {
      const state = getState();

      if(data && Array.isArray(data.resale_listings)) {
        dispatch(resaleListingsFetched(data.resale_listings));
      }

      if(state.ticketReservation.id || state.isCreatingTicketReservation) {
        return;
      }

      if (!calculatorRef || !calculatorRef.current) {
        return;
      }
      
      calculatorRef.current.updateGroupsAndTicketTypes({
        groups: data.ticket_group_availability,
        ticketTypes: data.ticket_types,
      });
      
      calculatorRef.current.reconcileCart();
      
      const calculatedResult = calculatorRef.current.calculate();

      let mutatedStates = new RealtimeDataMutator(
        {...data, ticket_types: calculatedResult.ticketTypes, add_ons: withHeldAddOnCounts(data.add_ons || [], activeRevealedHold(state.revealedHold, state.promoCode))},
        {...state.confirm},
        [...state.ticketTypes],
        {...state.ticketReservation},
        [...state.addOns],
        state.promoCode,
        state.startAtAddOns
      ).process();

      if(mutatedStates.confirm && mutatedStates.confirm.sold_out) {
        dispatch(confirmChanged(mutatedStates.confirm));
        dispatch(ticketReservationChanged(mutatedStates.ticketReservation || {}));

        if(mutatedStates.cartChanged) {
          dispatch(ticketTypesChanged(mutatedStates.ticketTypes));

          if(mutatedStates.addOns) {
            dispatch(addOnsChanged(mutatedStates.addOns));
          }

          toastError("We're sorry, this event has JUST sold out. Your cart has been updated.");
        } else {
          toastError("We're sorry, this event has JUST sold out.");
        }

        return;
      }

      if(mutatedStates.confirm && !mutatedStates.confirm.sold_out) {
        dispatch(confirmChanged(mutatedStates.confirm));
      }

      if(!mutatedStates.cartChanged) {
        // Ticket quantity options have changed
        if(mutatedStates.ticketTypes) {
          dispatch(ticketTypesChanged(mutatedStates.ticketTypes));
        }

        // AddOn quantity options have changed
        if(mutatedStates.addOns) {
          dispatch(addOnsChanged(mutatedStates.addOns));
        }
      } else {
        // Ticket quantity has changed in cart
        if(mutatedStates.ticketTypes) {
          dispatch(ticketTypesChanged(mutatedStates.ticketTypes));
        }

        // AddOn quantity has changed in cart
        if(mutatedStates.addOns) {
          dispatch(addOnsChanged(mutatedStates.addOns));
        }

        // Promo code has been removed
        let promoRevealedClear = null;
        if(mutatedStates.promoCodeRemoved) {
          dispatch(promoCodeChanged({}));
          promoRevealedClear = dispatch(clearPromoRevealedTiers(calculatorRef && calculatorRef.current, {
            ticketTypes: mutatedStates.ticketTypes ? mutatedStates.ticketTypes : state.ticketTypes,
            addOns: mutatedStates.addOns ? mutatedStates.addOns : state.addOns,
            userContext: state.userContext,
            startAtAddOns: state.startAtAddOns
          }));
          toastError("We're sorry, but your selected promo code JUST sold out. Your cart has been updated.");
        }

        if(mutatedStates.ticketTypesSoldOut) {
          dispatch(ticketReservationChanged({}));
          toastError("We're sorry, one or more of your selected tickets has JUST sold out. Your cart has been updated.");
        } else {
          // Rebuild ticket reservation
          dispatch(
            buildTicketReservation(
              dispatch,
              state.csrfToken,
              state.confirm,
              promoRevealedClear ? promoRevealedClear.clearedTicketTypes : (mutatedStates.ticketTypes ? mutatedStates.ticketTypes : state.ticketTypes),
              mutatedStates.promoCodeRemoved ? {} : state.promoCode,
              promoRevealedClear ? promoRevealedClear.clearedAddOns : (mutatedStates.addOns ? mutatedStates.addOns : state.addOns),
              state.ticketReservation,
              state.userContext
            )
          );

          if(mutatedStates.ticketTypes || mutatedStates.addOns) {
            toastError("We're sorry, the availability of one or more of your selected tickets has JUST changed. Your cart has been updated.");
          }
        }
      }
    };
  };
};

let promoCodeLookupsInFlight = {};

export const lookupPromoCode = (dispatch, confirm, name, ticketTypes, csrfToken, addOns, ticketReservation, userContext) => {
  return (dispatch, getState) => {
    const lookupKey = confirm.id + ":" + name;
    if (promoCodeLookupsInFlight[lookupKey]) {
      return promoCodeLookupsInFlight[lookupKey];
    }

    const lookup = axios.get("/confirms/" + confirm.id + "/web_orders/lookup_promo_code", {
        params: {
          name: name
        }
      })
      .then(({ data }) => {
        if (cartIsResaleOnly(getState().resaleOrderItems, getState().ticketTypes, getState().addOns)) return;

        if(data == null || !data.id){
          dispatch(promoCodeNameValidChanged(false, data && data.invalid_reason));
        } else {
          dispatch(promoCodeChanged(data));
          dispatch(buildTicketReservation(dispatch, csrfToken, confirm, getState().ticketTypes, data, getState().addOns, getState().ticketReservation, userContext));
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 429) {
          toastError("Too many code attempts right now. Wait a moment and try again.");
        } else {
          toastError("We couldn't check that code just now. Please try again.");
        }
      })
      .finally(() => {
        delete promoCodeLookupsInFlight[lookupKey];
      });

    promoCodeLookupsInFlight[lookupKey] = lookup;
    return lookup;
  };
};

export const promoCodeNameChanged = (name) => ({
  type: PROMO_CODE_NAME_CHANGED,
  name
});

export const promoCodeNameValidChanged = (valid, reason) => ({
  type: PROMO_CODE_NAME_VALID_CHANGED,
  valid,
  reason
});

export const promoCodeChanged = (promoCode) => ({
  type: PROMO_CODE_CHANGED,
  promoCode
});

export const buildAbstractOrderItems = (donationCustomChargeType) => {
  var abstractOrderItems = [];

  var submitDonation = (
    donationCustomChargeType
      && donationCustomChargeType.enteredPrice
      && donationCustomChargeType.enteredPrice.length > 0
      && parseFloatFromString(donationCustomChargeType.enteredPrice) > 0.50
  )

  if(submitDonation){
    abstractOrderItems.push({
        custom_charge_type_id: donationCustomChargeType.id,
        quantity: 1,
        price: donationCustomChargeType.price,
        face_value: donationCustomChargeType.price
    });
  }

  return abstractOrderItems;
}

const metaClickIdParams = (state) => {
  const fbclid = state.metaClickId;
  return fbclid && fbclid.length > 0 ? { fbclid: fbclid } : {};
};

const attributionVisitorParams = (state) => {
  const uuid = state.attributionVisitorUuid || sharedAttributionVisitorId();
  return { attribution_visitor_uuid: uuid || '' };
};

const withAttributionVisitor = (body, state) => {
  const params = attributionVisitorParams(state);
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    body.append('attribution_visitor_uuid', params.attribution_visitor_uuid);
    return body;
  }
  return { ...body, ...params };
};

let latestBuildRequest = 0;

export const buildTicketReservation = (dispatch, csrfToken, confirm, ticketTypes, promoCode, addOns, ticketReservation, userContext) => {
  return (dispatch, getState) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    const buildRequest = ++latestBuildRequest;

    // An add-on that needs another add-on leaves the cart when that add-on does.
    const clearedAddOns = clearUnmetAddOnRequirements(addOns, userContext);
    if (clearedAddOns.some((addOn, i) => addOn !== addOns[i])) {
      addOns = clearedAddOns;
      dispatch(addOnsChanged(addOns));
    }

    var orderItems = ticketTypes.filter((tt) =>
      tt.quantity && tt.quantity > 0
    );

    var addOnOrderItems = addOns.filter((ao) =>
      ao.quantity && ao.quantity > 0
    );

    var donationCustomChargeType = getState().donationCustomChargeType;
    var abstractOrderItems = buildAbstractOrderItems(donationCustomChargeType);

    var resaleOrderItemsAttributes = (getState().resaleOrderItems || [])
      .map((s) => ({ resale_listing_id: s.listingId, quantity: s.quantity }));

    var currentReservation = getState().ticketReservation || ticketReservation;

    if(orderItems.length === 0 && addOnOrderItems.length === 0 && abstractOrderItems.length === 0 && resaleOrderItemsAttributes.length === 0){
      var updated = {};

      if (currentReservation._screen){
        updated = Object.assign({}, updated, {
          _screen: currentReservation._screen,
          _screenHistory: currentReservation._screenHistory
        });
      }

      return dispatch(ticketReservationChanged(updated));
    }

    const channelsByEvent = readChannelsByEvent(promoCode);
    const revealedHold = activeRevealedHold(getState().revealedHold, promoCode);

    const selectedMembershipProduct = getState().selectedMembershipProduct;

    return axios.post("/confirms/" + confirm.id + "/ticket_reservations/build", {
        ...metaClickIdParams(getState()),
        ticket_reservation: {
          revealed_hold_ids: extractRevealedHoldId(revealedHold),
          revealed_hold_tokens: extractRevealedHoldToken(revealedHold),
          promo_code_name: (promoCode && promoCode.name ? promoCode.name : null),
          user_context_access_token: (userContext && userContext.access_token ? userContext.access_token : null),
          selected_membership_product_id: selectedMembershipProduct && selectedMembershipProduct.id ? selectedMembershipProduct.id : null,
          selected_membership_product_price_id: selectedMembershipProduct?.ticketing_price?.id || null,
          verb: getState().verb,
          channels_by_event: channelsByEvent,
          resale_order_items_attributes: resaleOrderItemsAttributes,
          price_quote_token: getState().priceQuoteToken,
          price_quote_visitor_id: getState().priceQuoteVisitorId,
          order_items_attributes: orderItems.map((item) => {
            return {
              ticket_type_id: item.id,
              quantity: item.quantity,
              seatsio_objects: item.seatsIOObjects
            };
          }),
          add_on_order_items_attributes: addOnOrderItems.map((item) => {
            return {
              add_on_id: item.id,
              quantity: item.quantity,
              seatsio_objects: item.seatsIOObjects
            };
          }),
          abstract_order_items_attributes: abstractOrderItems.map((item) => {
            return {
              custom_charge_type_id: item.custom_charge_type_id,
              quantity: item.quantity,
              price: item.price,
              face_value: item.face_value
            };
          })
        }
      })
      .then(({ data }) => {
        if(buildRequest !== latestBuildRequest){
          return;
        }

        const latestReservation = getState().ticketReservation || ticketReservation;
        if(latestReservation.id){
          return;
        }

        if(latestReservation._screen){
          data = Object.assign({}, data, {_screen: latestReservation._screen, _screenHistory: latestReservation._screenHistory});
        }

        dispatch(ticketReservationChanged(data));
      })
      .catch((error) => {
        if(buildRequest !== latestBuildRequest){
          return;
        }

        handleReservationError(error);
      });
  };
};

export const ticketReservationChanged = (ticketReservation) => ({
  type: TICKET_RESERVATION_CHANGED,
  ticketReservation
});

export const ticketTypesChanged = (ticketTypes) => ({
  type: TICKET_TYPES_CHANGED,
  ticketTypes
});

export const createTicketReservation = (dispatch, csrfToken, confirm, ticketTypes, promoCode, addOns, ticketReservation, seatsIOChart, customerioId, userContext, parentUrl) => {
  return (dispatch, getState) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    dispatch(cartStartBusyMessageChanged(null));
    dispatch(isCreatingTicketReservationChanged(true));

    var orderItems = ticketTypes.filter((tt) =>
      tt.quantity && tt.quantity > 0
    );

    var addOnOrderItems = addOns.filter((ao) =>
      ao.quantity && ao.quantity > 0
    );

    var seatsioHoldTokens = [];

    if(orderItems.some((i) => i.seatsIOObjects && i.seatsIOObjects.length > 0)){
      seatsioHoldTokens = [...seatsioHoldTokens, seatsIOChart.holdToken];
    }

    var addOnHoldTokens = [
      ...new Set(
        addOnOrderItems
          .filter((item) => item.seatsIOObjects)
          .map((item) => item.seatsIOObjects)
          .flat()
          .map((object) => object.hold_token)
      )
    ];

    seatsioHoldTokens = [
      ...seatsioHoldTokens,
      ...addOnHoldTokens
    ];

    var donationCustomChargeType = getState().donationCustomChargeType;
    var abstractOrderItems = buildAbstractOrderItems(donationCustomChargeType);

    var resaleOrderItemsAttributes = (getState().resaleOrderItems || [])
      .map((s) => ({ resale_listing_id: s.listingId, quantity: s.quantity }));

    const channelsByEvent = readChannelsByEvent(promoCode);
    const revealedHold = activeRevealedHold(getState().revealedHold, promoCode);
    const selectedMembershipProduct = getState().selectedMembershipProduct;

    const membershipParams = selectedMembershipProduct?.ticketing_price
      ? { selected_membership_product_id: selectedMembershipProduct.id, selected_membership_product_price_id: selectedMembershipProduct.ticketing_price.id }
      : {};

    return postCartStart("/confirms/" + confirm.id + "/ticket_reservations", {
        ...metaClickIdParams(getState()),
        ...attributionVisitorParams(getState()),
        ticket_reservation: {
          seatsio_hold_tokens: seatsioHoldTokens,
          revealed_hold_ids: extractRevealedHoldId(revealedHold),
          revealed_hold_tokens: extractRevealedHoldToken(revealedHold),
          promo_code_name: (promoCode && promoCode.name ? promoCode.name : null),
          user_context_access_token: (userContext && userContext.access_token ? userContext.access_token : null),
          customerio_id: (customerioId && customerioId.length > 0 ? customerioId : null),
          parent_url: (parentUrl && parentUrl.length > 0 ? parentUrl : null),
          checkout_token: (getState().checkoutToken || null),
          verb: getState().verb,
          channels_by_event: channelsByEvent,
          resale_order_items_attributes: resaleOrderItemsAttributes,
          price_quote_token: getState().priceQuoteToken,
          price_quote_visitor_id: getState().priceQuoteVisitorId,
          ...membershipParams,
          order_items_attributes: orderItems.map((item) => {
            return {
              ticket_type_id: item.id,
              quantity: item.quantity,
              seatsio_objects: item.seatsIOObjects
            };
          }),
          add_on_order_items_attributes: addOnOrderItems.map((item) => {
            return {
              add_on_id: item.id,
              quantity: item.quantity,
              seatsio_objects: item.seatsIOObjects
            };
          }),
          abstract_order_items_attributes: abstractOrderItems.map((item) => {
            return {
              custom_charge_type_id: item.custom_charge_type_id,
              quantity: item.quantity,
              price: item.price,
              face_value: item.face_value
            };
          })
        }
      })
      .then(({ data }) => {
        if(data.user_context_access_token){
          data = Object.assign({}, data, {
            confirm_email: data.email
          });
        }

        dispatch(ticketReservationChanged(data));
      })
      .catch((error) => {
        if (cartStartBusy(error)) {
          dispatch(cartStartBusyMessageChanged(error.response.data.join(", ")));
        } else {
          handleReservationError(error);
        }
      })
      .then(() => {
        dispatch(isCreatingTicketReservationChanged(false));
      });
  };
};

export const deleteTicketReservation = (dispatch, csrfToken, confirm, ticketReservation) => {
  return dispatch => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.delete("/confirms/" + confirm.id + "/ticket_reservations/" + ticketReservation.token)
      .then(({ data }) => {
        dispatch(cancelCheckout());
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };
};

export const cancelCheckout = () => ({
  type: CANCEL_CHECKOUT
});

export const stripePromiseChanged = (stripePromise) => ({
  type: STRIPE_PROMISE_CHANGED,
  stripePromise
});

export const stripeErrorChanged = (error) => ({
  type: STRIPE_ERROR_CHANGED,
  error
});

export const updateTicketReservation = (dispatch, csrfToken, confirm, ticketReservation, onSuccess, customFieldValues, acceptedVenueAgreementIds = [], resaleTermsAccepted) => {
  return (dispatch, getState) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.patch("/confirms/" + confirm.id + "/ticket_reservations/" + ticketReservation.token, withAttributionVisitor(requestBodyWithFiles('ticket_reservation', {
          first_name: ticketReservation.first_name,
          last_name: ticketReservation.last_name,
          email: ticketReservation.email,
          subscribe_to_venue: ticketReservation.subscribe_to_venue,
          custom_field_values_attributes: customFieldValues.map((customFieldValue) => customFieldValueToParams(customFieldValue)),
          accepted_venue_agreement_ids: acceptedVenueAgreementIds,
          ...(resaleTermsAccepted === undefined ? {} : { resale_terms_accepted: resaleTermsAccepted }),
          checkout_captcha_token: ticketReservation.checkout_captcha_token
      }), getState()))
      .then(({ data }) => {
        var updated = Object.assign({}, ticketReservation, data);

        customFieldValues.map((cfv) => {
          var persisted = data.custom_field_values.find((value) =>
            value.custom_field && sameAnswer(value, cfv)
          );

          if(persisted){
            var updatedCustomFieldValue = Object.assign({}, cfv, {
              id: persisted.id
            });

            dispatch(customFieldValueChanged(updatedCustomFieldValue));
          }
        });

        if(updated.in_memory_fees === null){
          updated = Object.assign({}, updated, {
            in_memory_fees: ticketReservation.in_memory_fees
          });
        }

        dispatch(ticketReservationChanged(updated));
        onSuccess(updated);
      })
      .catch((error) => {
        dispatch(isPlacingOrderChanged(false));

        if (!error.response && window.Sentry) window.Sentry.captureException(error);

        toast.error(Array.isArray(error.response?.data) ? error.response.data.join(", ") : "Something went wrong. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };
};

export const autoSaveTicketReservation = (dispatch, csrfToken, confirm, ticketReservation) => {
  return dispatch => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    var params = {
      first_name: ticketReservation.first_name,
      last_name: ticketReservation.last_name,
      email: null,
      contact_zip: null
    }

    if(validateEmail(ticketReservation.email)){
      params = Object.assign({}, params, {
        email: ticketReservation.email
      });
    }

    if(validateZip(ticketReservation.contact_zip)){
      params = Object.assign({}, params, {
        contact_zip: ticketReservation.contact_zip
      });
    }

    return axios.patch("/confirms/" + confirm.id + "/ticket_reservations/" + ticketReservation.token, {
        ticket_reservation: params
      })
      .then(({ data }) => {
        // ...
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          toastId: "ticket-reservation-autosave",
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };
};

export const saveGeoCodedStateCode = (dispatch, csrfToken, confirm, ticketReservation, stateCode) => {
  return (dispatch, getState) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    var params = {
      contact_state_code: stateCode
    }

    return axios.patch("/confirms/" + confirm.id + "/ticket_reservations/" + ticketReservation.token, {
        ticket_reservation: params
      })
      .then(({ data }) => {
        var currentTicketReservation = getState().ticketReservation;
        var updated = Object.assign({}, currentTicketReservation, {
          contact_state_code: data.contact_state_code
        });

        dispatch(ticketReservationChanged(updated));
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };
};

export const checkoutTimePercentageRemainingChanged = (percentage) => ({
  type: CHECKOUT_TIME_PERCENTAGE_REMAINING_CHANGED,
  percentage
});

export const timeLimitReachedChanged = (reached) => ({
  type: TIME_LIMIT_REACHED_CHANGED,
  reached
});

export const isPlacingOrderChanged = (isPlacingOrder) => ({
  type: IS_PLACING_ORDER_CHANGED,
  isPlacingOrder
});

export const addOnsChanged = (addOns) => ({
  type: ADD_ONS_CHANGED,
  addOns
});

export const gtagChanged = (gtag) => ({
  type: GTAG_CHANGED,
  gtag
});

export const customFieldValueChanged = (customFieldValue) => ({
  type: CUSTOM_FIELD_VALUE_CHANGED,
  customFieldValue
});

export const seatsIOChartChanged = (seatsIOChart) => ({
  type: SEATS_IO_CHART_CHANGED,
  seatsIOChart
});

export const seatsIOSelectedObjectsChanged = (seatsIOSelectedObjects) => ({
  type: SEATS_IO_SELECTED_OBJECTS_CHANGED,
  seatsIOSelectedObjects
});

export const customerioIdChanged = (customerioId) => ({
  type: CUSTOMERIO_ID_CHANGED,
  customerioId
});

export const parentUrlChanged = (parentUrl) => ({
  type: PARENT_URL_CHANGED,
  parentUrl
});

export const originAttributionChanged = (originAttribution) => ({
  type: ORIGIN_ATTRIBUTION_CHANGED,
  originAttribution
});

export const metaClickIdChanged = (metaClickId) => ({
  type: META_CLICK_ID_CHANGED,
  metaClickId
});

export const attributionVisitorChanged = (attributionVisitorUuid) => ({
  type: ATTRIBUTION_VISITOR_CHANGED,
  attributionVisitorUuid
});

export const reportParentUrlForUtmBeacon = (confirmId) => (dispatch, getState) => {
  const { checkoutToken, parentUrl, originAttribution } = getState();
  if (!checkoutToken || !parentUrl) return Promise.resolve();

  const origin = originAttribution || {};
  return axios
    .post(`/confirms/${confirmId}/web_orders/utm_beacon`, {
      checkout_token: checkoutToken,
      parent_url: parentUrl,
      origin_referrer: origin.referrer || "",
      origin_utm_source: origin.utmSource || "",
      origin_utm_medium: origin.utmMedium || "",
      origin_utm_campaign: origin.utmCampaign || "",
    })
    .catch(() => {
      // beacon is best-effort; never break the checkout if it fails
    });
};

export const isCreatingTicketReservationChanged = (isCreatingTicketReservation) => ({
  type: IS_CREATING_TICKET_RESERVATION_CHANGED,
  isCreatingTicketReservation
});

export const cartStartBusyMessageChanged = (cartStartBusyMessage) => ({
  type: CART_START_BUSY_MESSAGE_CHANGED,
  cartStartBusyMessage
});

export const registerTicketInsurance = (dispatch, csrfToken, confirm, ticketReservation, quote) => {
  return dispatch => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.post("/confirms/" + confirm.id + "/ticket_insurances/" + quote.quote_id + "/register", {
        ticket_insurance: {
          ticket_reservation_id: ticketReservation.token,
          premium_amount_in_cents: quote.premium_amount
        }
      })
      .then(({ data }) => {
        dispatch(ticketInsuranceChanged(data));
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };
};

export const insuranceOfferStateChanged = (insuranceOfferState) => ({
  type: INSURANCE_OFFER_STATE_CHANGED,
  insuranceOfferState
});

export const ticketInsuranceChanged = (ticketInsurance) => ({
  type: TICKET_INSURANCE_CHANGED,
  ticketInsurance
});

export const deleteTicketInsurance = (dispatch, csrfToken, confirm, ticketInsurance) => {
  return dispatch => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    if(!ticketInsurance.quote_id){
      return;
    }

    return axios.delete("/confirms/" + confirm.id + "/ticket_insurances/" + ticketInsurance.quote_id)
      .then(({ data }) => {
        dispatch(ticketInsuranceChanged({}));
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };
};

export const hasInsuranceQuoteChanged = (hasInsuranceQuote) => ({
  type: HAS_INSURANCE_QUOTE_CHANGED,
  hasInsuranceQuote
});

export const donationCustomChargeTypeChanged = (donationCustomChargeType) => ({
  type: DONATION_CUSTOM_CHARGE_TYPE_CHANGED,
  donationCustomChargeType
});

export const userContextChanged = (userContext) => ({
  type: USER_CONTEXT_CHANGED,
  userContext
});

export const membershipProductsChanged = (membershipProducts) => ({
  type: MEMBERSHIP_PRODUCTS_CHANGED,
  membershipProducts
});

export const selectedMembershipProductChanged = (selectedMembershipProduct) => ({
  type: SELECTED_MEMBERSHIP_PRODUCT_CHANGED,
  selectedMembershipProduct
});

export const fetchMembershipProducts = (confirm, userContext) => {
  return dispatch => {
    const params = userContext && userContext.access_token ? { ucat: userContext.access_token } : {};
    return axios.get("/confirms/" + confirm.id + "/web_orders/membership_products", { params })
      .then(({ data }) => {
        dispatch(membershipProductsChanged(data));
      })
      .catch(() => {
        toastError("Unable to load membership options. Please try refreshing the page.");
        dispatch(membershipProductsChanged([]));
      });
  };
};

export const resaleListingsFetched = (resaleListings) => ({
  type: RESALE_LISTINGS_FETCHED,
  resaleListings
});

export const clearPromoRevealedTiers = (calculator, { ticketTypes, addOns, userContext, startAtAddOns }) => (dispatch) => {
  const clearedTicketTypes = (ticketTypes || []).map((ticketType) => (
    ticketType.visibility === "Hidden"
      ? Object.assign({}, ticketType, {quantity: 0})
      : ticketType
  ));
  dispatch(ticketTypesChanged(clearedTicketTypes));

  clearHiddenTiersInCalculator(calculator, clearedTicketTypes);

  const stillVisibleAddOnIds = visibleAddOns(
    addOns || [], clearedTicketTypes, userContext, startAtAddOns
  ).map((addOn) => addOn.id);
  const clearedAddOns = (addOns || []).map((addOn) => (
    stillVisibleAddOnIds.includes(addOn.id)
      ? addOn
      : Object.assign({}, addOn, {quantity: 0})
  ));
  dispatch(addOnsChanged(clearedAddOns));

  return { clearedTicketTypes, clearedAddOns };
};

export const resaleQuantityChanged = (listingId, quantity, ticketTypeName, listPriceCents, buyerTotalPerTicketCents, calculatorRef) => (dispatch, getState) => {
  dispatch({
    type: RESALE_QUANTITY_CHANGED,
    listingId,
    quantity,
    ticketTypeName,
    listPriceCents,
    buyerTotalPerTicketCents
  });

  let state = getState();

  if (cartIsResaleOnly(state.resaleOrderItems, state.ticketTypes, state.addOns) && Object.keys(state.promoCode || {}).length > 0) {
    dispatch(promoCodeChanged({}));

    dispatch(clearPromoRevealedTiers(calculatorRef && calculatorRef.current, {
      ticketTypes: state.ticketTypes,
      addOns: state.addOns,
      userContext: state.userContext,
      startAtAddOns: state.startAtAddOns
    }));

    state = getState();
  }

  dispatch(
    buildTicketReservation(
      dispatch,
      state.csrfToken,
      state.confirm,
      state.ticketTypes,
      state.promoCode,
      state.addOns,
      state.ticketReservation,
      state.userContext
    )
  );
};

const applyOwnerPromoCodeLookup = (dispatch, getState, { confirm, calculatorRef, params, endpoint, flag, catchLabel }) => {
  if (Object.keys(params).length === 0) {
    const state = getState();
    if (state.promoCode && state.promoCode[flag]) {
      dispatch(promoCodeChanged({}));
      const { clearedTicketTypes, clearedAddOns } = dispatch(clearPromoRevealedTiers(calculatorRef && calculatorRef.current, {
        ticketTypes: state.ticketTypes,
        addOns: state.addOns,
        userContext: state.userContext,
        startAtAddOns: state.startAtAddOns
      }));
      const hasTicketsInCart = clearedTicketTypes.some((tt) => tt.quantity && tt.quantity > 0)
        || clearedAddOns.some((ao) => ao.quantity && ao.quantity > 0);
      if (hasTicketsInCart) {
        dispatch(buildTicketReservation(dispatch, state.csrfToken, confirm, clearedTicketTypes, {}, clearedAddOns, state.ticketReservation, state.userContext));
      }
    }
    return Promise.resolve();
  }

  return axios.get("/confirms/" + confirm.id + endpoint, { params })
    .then(({ data }) => {
      const state = getState();
      if (state.promoCode && state.promoCode.id && !state.promoCode[flag]) return;
      if (cartIsResaleOnly(state.resaleOrderItems, state.ticketTypes, state.addOns)) return;

      const foundPromoCode = data && data.id ? { ...data, [flag]: true } : null;
      if (!foundPromoCode && !(state.promoCode && state.promoCode[flag])) return;

      const nextPromoCode = foundPromoCode || {};
      dispatch(promoCodeChanged(nextPromoCode));

      let ticketTypesForBuild = state.ticketTypes;
      let addOnsForBuild = state.addOns;
      if (!foundPromoCode) {
        const cleared = dispatch(clearPromoRevealedTiers(calculatorRef && calculatorRef.current, {
          ticketTypes: state.ticketTypes,
          addOns: state.addOns,
          userContext: state.userContext,
          startAtAddOns: state.startAtAddOns
        }));
        ticketTypesForBuild = cleared.clearedTicketTypes;
        addOnsForBuild = cleared.clearedAddOns;
      }

      const hasTicketsInCart = ticketTypesForBuild.some(tt => tt.quantity && tt.quantity > 0)
        || addOnsForBuild.some(ao => ao.quantity && ao.quantity > 0);
      if (hasTicketsInCart) {
        dispatch(buildTicketReservation(dispatch, state.csrfToken, confirm, ticketTypesForBuild, nextPromoCode, addOnsForBuild, state.ticketReservation, state.userContext));
      }
    })
    .catch((error) => console.log(catchLabel, error));
};

export const fetchMembershipPromoCode = (confirm, userContext, selectedMembershipProduct, calculatorRef) => {
  return (dispatch, getState) => {
    const params = {};
    if (userContext && userContext.access_token) params.ucat = userContext.access_token;
    if (selectedMembershipProduct && selectedMembershipProduct.id) params.selected_membership_product_id = selectedMembershipProduct.id;
    return applyOwnerPromoCodeLookup(dispatch, getState, {
      confirm,
      calculatorRef,
      params,
      endpoint: "/web_orders/lookup_membership_promo_code",
      flag: "_membershipApplied",
      catchLabel: "Failed to fetch membership promo code"
    });
  };
};

// O-3930 WS10. Auto-reveal a logged-in pack owner's hidden $0 pack ticket on load,
// mirroring fetchMembershipPromoCode. lookup_pass_promo_code returns the owner's
// PromoCodes::Pass (owner-scoped by ucat) whose ticket_type_ids reveal the pack
// type via utils/visibleTicketTypes.js; the reservation then carries promo_code_name
// so the server applies it and the admission checks out at $0.
export const fetchPassPromoCode = (confirm, userContext, calculatorRef) => {
  return (dispatch, getState) => {
    const params = {};
    if (userContext && userContext.access_token) params.ucat = userContext.access_token;
    return applyOwnerPromoCodeLookup(dispatch, getState, {
      confirm,
      calculatorRef,
      params,
      endpoint: "/web_orders/lookup_pass_promo_code",
      flag: "_passApplied",
      catchLabel: "Failed to fetch pass promo code"
    });
  };
};

// Used by the inline widget builder to push live widgetConfig /
// checkoutCategories changes into Redux without remounting the entire app
// (which would clear cart state).
export const widgetConfigReplaced = (widgetConfig) => ({
  type: WIDGET_CONFIG_REPLACED,
  widgetConfig
});

export const checkoutCategoriesReplaced = (checkoutCategories) => ({
  type: CHECKOUT_CATEGORIES_REPLACED,
  checkoutCategories
});
